import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { reportsPosition } from '../GscTools/metrics';
import { PRESET } from './thresholds';

/**
 * The CSV of a review: the candidates, and the criteria that selected them.
 *
 * The criteria lead the file rather than trailing it, because a queue without them is a list of
 * queries somebody has to take on trust. Everything that bounded the review travels with it — the
 * property, the window, the surface, the filters, how many rows were retrieved to choose from,
 * and when. A row's absent metric stays empty; the reason it is absent is a column of its own.
 */
export function buildReviewCsv({ t, scope, thresholds, candidates, retrievedRows, fetchedAt }) {
    const hasPosition = reportsPosition(scope.searchType);

    const meta = [
        [t('tools.search_opportunities.export.report'), t('tools.search_opportunities.header')],
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.controls.search_type'), searchTypeLabel(scope.searchType, t)],
        [t('tools.gsc.controls.country'), scope.country || 'all'],
        [t('tools.gsc.controls.device'), scope.device || 'all'],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [
            t('tools.search_opportunities.export.preset'),
            t(`tools.search_opportunities.presets.${thresholds.preset}`),
        ],
        [
            t('tools.search_opportunities.criteria.position'),
            `${thresholds.minPosition}–${thresholds.maxPosition}`,
        ],
        [t('tools.search_opportunities.criteria.impressions'), thresholds.minImpressions],
        ...(thresholds.preset === PRESET.LOW_CTR
            ? [[t('tools.search_opportunities.criteria.ctr'), thresholds.maxCtr]]
            : []),
        [t('tools.search_opportunities.export.retrieved'), retrievedRows],
        [t('tools.search_opportunities.export.candidates'), candidates.length],
        [t('tools.search_opportunities.export.meaning'), t('tools.search_opportunities.meaning')],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
        [],
    ];

    const header = [
        t('tools.gsc.columns.query'),
        t('tools.gsc.metrics.clicks_short'),
        t('tools.gsc.metrics.impressions_short'),
        t('tools.gsc.metrics.ctr_short'),
        ...(hasPosition ? [t('tools.gsc.metrics.position_short')] : []),
        t('tools.search_opportunities.candidates.reason'),
    ];

    const body = candidates.map((row) => [
        row.key,
        row.clicks,
        row.impressions,
        row.ctr,
        ...(hasPosition ? [row.position] : []),
        row.reason,
    ]);

    return [...meta, header, ...body];
}
